import React from 'react';

import { importComponent, isServer } from '@xingternal/hops';
import { useExperiment } from '@xingternal/hub';

import { UniversalRedirect } from '@/components/_common';
import { FullPageDotLoading } from '@/components/full-page-dot-loading';
import { JobPostingTrackingProvider } from '@/components/job-posting/contexts';
import { routes } from '@/config';
import { HeaderContextProvider } from '@/domain/job-details/components/header/context/header-context';
import { JobPostingVisibleJobFragment, useJobDetailQuery } from '@/graphql/generated';
import { useLoginUrl } from '@/hooks';

import { withSimilarJobs } from './components/withSimilarJobs';
import { getClientErrorComponent, getServerErrorComponent, isFencedProjobs, isPrivate } from './helpers';

type JobPostingWithSimilarJobsProps = {
  job: JobPostingVisibleJobFragment;
};
const JobPostingWithSimilarJobs: React.ComponentType<JobPostingWithSimilarJobsProps> = importComponent(
  () => import('@/components/job-posting/job-posting-component'),
  ({ JobPostingComponent }) => withSimilarJobs(JobPostingComponent)
);

type Props = {
  slug: string;
};

export const JobPostingContainer: React.VFC<Props> = ({ slug }) => {
  const loginUrl = useLoginUrl();
  const jobSummaryExperimentVariant = useExperiment('ABACUS-413');
  const includeSummary = !!jobSummaryExperimentVariant && ['B', 'C'].includes(jobSummaryExperimentVariant);
  const { loading, error, data } = useJobDetailQuery({
    variables: { slug, includeSummary },
    errorPolicy: 'all',
  });

  const jobData = data?.job as JobPostingVisibleJobFragment;

  if (loading) return <FullPageDotLoading />;

  const hasBasicRequiredData =
    data?.job?.__typename === 'UnauthorizedJob' ||
    (!!jobData?.title &&
      !!jobData?.date &&
      !!jobData?.location?.country &&
      !!jobData?.companyInfo?.companyNameOverride &&
      !!jobData?.description);

  if (!hasBasicRequiredData) {
    const ErrorComponent = isServer
      ? getServerErrorComponent({ error, data })
      : getClientErrorComponent({ error, data });
    if (ErrorComponent) return ErrorComponent;
  }

  if (isFencedProjobs(data)) {
    return <UniversalRedirect status={302} to={routes.upsellProjobs} />;
  } else if (isPrivate(data)) {
    return <UniversalRedirect to={loginUrl} />;
  }
  // TS is not able to infer the job type from validation above
  return (
    <HeaderContextProvider job={data?.job as JobPostingVisibleJobFragment}>
      <JobPostingTrackingProvider job={data?.job as JobPostingVisibleJobFragment} key={data?.job?.id}>
        <JobPostingWithSimilarJobs job={data?.job as JobPostingVisibleJobFragment} key={data?.job?.id} />
      </JobPostingTrackingProvider>
    </HeaderContextProvider>
  );
};
